import { lazy, memo } from "react";
import { loanDrilldownSidebarTypes } from "../../../constants/enums/loanDrilldownScreenSidebarOptions";
import DrilldownCreateNewItemSidebar from "./types/DrilldownCreateNewItemSidebar";
import DrilldownSidebarAddEditLoanCondition from "./types/DrilldownSidebarAddEditLoanCondition";
import DrilldownSidebarAddEditLoanConditionRedesign from "./types/DrilldownSidebarAddEditLoanConditionRedesign";
import DrilldownSidebarUpdateBorrowerData from "./types/DrilldownSidebarUpdateBorrowerData";
import DrilldownSidebarEmailComposer from "./types/DrilldownSidebarEmailComposer";

// Lazy load sidebar components
const DrilldownFilePreviewSidebar = lazy(
  () => import("./types/DrilldownFilePreviewSidebar"),
);

const DrilldownSidebarAddBorrower = lazy(
  () => import("./types/DrilldownSidebarAddBorrower"),
);
const DrilldownSidebarAddLoanRelatedContact = lazy(
  () => import("./types/DrilldownSidebarAddLoanRelatedContact"),
);
const DrilldownSidebarAddException = lazy(
  () => import("./types/DrilldownSidebarAddException"),
);
const DrilldownSidebarAddProperty = lazy(
  () => import("./types/DrilldownSidebarAddProperty"),
);

const DrilldownSidebarEnhancedComments = lazy(
  () => import("./types/DrilldownSidebarEnhancedComments"),
);
const DrilldownSidebarLoanChangeHistory = lazy(
  () => import("./types/DrilldownSidebarLoanChangeHistory"),
);
const DrilldownSidebarFormSpy = lazy(
  () => import("./types/DrilldownSidebarFormSpy"),
);
const DrilldownSidebarKeyDatesAndTotals = lazy(
  () => import("./types/DrilldownSidebarKeyDatesAndTotals"),
);
const DrilldownSidebarUnderwritingChecklist = lazy(
  () => import("./types/DrilldownSidebarUnderwritingChecklist"),
);
const DrilldownSidebarUnderwritingNotes = lazy(
  () => import("./types/DrilldownSidebarUnderwritingNotes"),
);
const DrilldownSidebarProcessingNotes = lazy(
  () => import("./types/DrilldownSidebarProcessingNotes"),
);
// ----
const DrilldownSidebarAddLoanFee = lazy(
  () => import("./types/DrilldownSidebarAddLoanFee"),
);

const DrilldownSidebarAddEditLoanCredit = lazy(
  () => import("./types/DrilldownSidebarAddEditLoanCredit"),
);
const DrilldownSidebarAddEditTermSpecialConditions = lazy(
  () => import("./types/DrilldownSidebarAddEditTermSpecialConditions"),
);
const DrilldownSidebarAddEditBrokerTerms = lazy(
  () => import("./types/DrilldownSidebarAddEditBrokerTerms"),
);
const DrilldownSidebarAddEditCorrespondentFee = lazy(
  () => import("./types/DrilldownSidebarAddEditCorrespondentFee"),
);
const DrilldownSidebarAddEditThirdPartyFee = lazy(
  () => import("./types/DrilldownSidebarAddEditThirdPartyFee"),
);
const DrilldownSidebarAddEditEscrowItem = lazy(
  () => import("./types/DrilldownSidebarAddEditEscrowItem"),
);
const DrilldownSidebarAddDraw = lazy(
  () => import("./types/DrilldownSidebarAddDraw"),
);
const DrilldownSidebarAddEditEntity = lazy(
  () => import("./types/DrilldownSidebarAddEditEntity"),
);
const DrilldownSidebarAddEditEntityMember = lazy(
  () => import("./types/DrilldownSidebarAddEditEntityMember"),
);
const DrilldownSidebarAddEditLiquiditySource = lazy(
  () => import("./types/DrilldownSidebarAddEditLiquiditySource"),
);
// Two DSCR drawers, not one — income (a unit's rent) and expense (a
// property's carrying cost) have different anatomy (owner 2026-09-15).
const DrilldownSidebarAddEditDscrIncomeSource = lazy(
  () => import("./types/dscrSource/DrilldownSidebarAddEditDscrIncomeSource"),
);
const DrilldownSidebarAddEditDscrExpenseSource = lazy(
  () => import("./types/dscrSource/DrilldownSidebarAddEditDscrExpenseSource"),
);
// The DSCR Workbook redesign's own row drawer.
const DrilldownSidebarDscrRowRedesign = lazy(
  () => import("./types/dscrSource/DrilldownSidebarDscrRowRedesign"),
);
const DrilldownSidebarOrderFloodCert = lazy(
  () => import("./types/DrilldownSidebarOrderFloodCert"),
);
const DrilldownSidebarOrderFundingShield = lazy(
  () => import("./types/DrilldownSidebarOrderFundingShield"),
);
const DrilldownSidebarOrderInternalAppraisal = lazy(
  () => import("./types/DrilldownSidebarOrderInternalAppraisal"),
);
const DrilldownSidebarOrderOutsideValuation = lazy(
  () => import("./types/DrilldownSidebarOrderOutsideValuation"),
);
const DrilldownSidebarLoanLock = lazy(
  () => import("./types/DrilldownSidebarLoanLock"),
);
const DrilldownSidebarConditions = lazy(
  () => import("./types/DrilldownSidebarConditions"),
);
const DrilldownSidebarDocuments = lazy(
  () => import("./types/DrilldownSidebarDocuments"),
);
const DrilldownSidebarSizerUpload = lazy(
  () => import("./types/DrilldownSidebarSizerUpload"),
);
const DrilldownSidebarDocumentUploadPicker = lazy(
  () => import("./types/DrilldownSidebarDocumentUploadPicker"),
);

const DrilldownSidebarContentRenderer = memo(({ type }) => {
  switch (type) {
    case loanDrilldownSidebarTypes.CREATE_NEW:
      return <DrilldownCreateNewItemSidebar />;
    case loanDrilldownSidebarTypes.SIZER_UPLOAD:
      return <DrilldownSidebarSizerUpload />;
    case loanDrilldownSidebarTypes.UW_STIPS_AND_CONDITIONS:
      return <DrilldownSidebarConditions />;
    case loanDrilldownSidebarTypes.DOCUMENTS:
      return <DrilldownSidebarDocuments />;
    case loanDrilldownSidebarTypes.DOCUMENT_UPLOAD_PICKER:
      return <DrilldownSidebarDocumentUploadPicker />;
    case loanDrilldownSidebarTypes.FILE_PREVIEW:
      return <DrilldownFilePreviewSidebar />;

    case loanDrilldownSidebarTypes.FORMSPY:
      return <DrilldownSidebarFormSpy />;
    case loanDrilldownSidebarTypes.COMMENTS:
      return <DrilldownSidebarEnhancedComments />;

    case loanDrilldownSidebarTypes.UW_NOTES:
      return <DrilldownSidebarUnderwritingNotes />;
    case loanDrilldownSidebarTypes.PROCESSING_NOTES:
      return <DrilldownSidebarProcessingNotes />;

    case loanDrilldownSidebarTypes.UW_CHECKLIST:
      return <DrilldownSidebarUnderwritingChecklist />;
    case loanDrilldownSidebarTypes.ADD_BORROWER:
      return <DrilldownSidebarAddBorrower />;
    case loanDrilldownSidebarTypes.EDIT_BORROWER:
      return <DrilldownSidebarUpdateBorrowerData />;
    case loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT:
      return <DrilldownSidebarAddLoanRelatedContact />;
    case loanDrilldownSidebarTypes.ADD_LOAN_FEE:
      return <DrilldownSidebarAddLoanFee />;
    case loanDrilldownSidebarTypes.ADD_LOAN_CREDIT:
      return <DrilldownSidebarAddEditLoanCredit />;
    case loanDrilldownSidebarTypes.ADD_LOAN_SPECIAL_CONDITION:
      return <DrilldownSidebarAddEditTermSpecialConditions />;
    case loanDrilldownSidebarTypes.ADD_EDIT_BROKER_TERMS:
      return <DrilldownSidebarAddEditBrokerTerms />;
    case loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION:
      return <DrilldownSidebarAddEditLoanCondition />;
    case loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION_REDESIGN:
      return <DrilldownSidebarAddEditLoanConditionRedesign />;
    case loanDrilldownSidebarTypes.ADD_EDIT_CORRESPONDENT_FEE:
      return <DrilldownSidebarAddEditCorrespondentFee />;
    case loanDrilldownSidebarTypes.ADD_EDIT_THIRD_PARTY_FEES:
      return <DrilldownSidebarAddEditThirdPartyFee />;
    case loanDrilldownSidebarTypes.ADD_LOAN_ESCROW_ITEM:
      return <DrilldownSidebarAddEditEscrowItem />;
    case loanDrilldownSidebarTypes.ADD_DRAW:
      return <DrilldownSidebarAddDraw />;
    case loanDrilldownSidebarTypes.KEY_DATES_AND_TOTALS:
      return <DrilldownSidebarKeyDatesAndTotals />;
    case loanDrilldownSidebarTypes.LOAN_CHANGE_HISTORY:
      return <DrilldownSidebarLoanChangeHistory />;
    case loanDrilldownSidebarTypes.ADD_EXCEPTION:
      return <DrilldownSidebarAddException />;
    case loanDrilldownSidebarTypes.ADD_PROPERTY:
      return <DrilldownSidebarAddProperty />;
    case loanDrilldownSidebarTypes.ADD_EDIT_ENTITY:
      return <DrilldownSidebarAddEditEntity />;
    case loanDrilldownSidebarTypes.ADD_EDIT_ENTITY_MEMBER:
      return <DrilldownSidebarAddEditEntityMember />;
    case loanDrilldownSidebarTypes.ADD_EDIT_LIQUIDITY_SOURCE:
      return <DrilldownSidebarAddEditLiquiditySource />;
    case loanDrilldownSidebarTypes.ADD_EDIT_DSCR_INCOME_SOURCE:
      return <DrilldownSidebarAddEditDscrIncomeSource />;
    case loanDrilldownSidebarTypes.ADD_EDIT_DSCR_EXPENSE_SOURCE:
      return <DrilldownSidebarAddEditDscrExpenseSource />;
    case loanDrilldownSidebarTypes.DSCR_ROW_REDESIGN:
      return <DrilldownSidebarDscrRowRedesign />;
    case loanDrilldownSidebarTypes.ORDER_FLOOD_CERT:
      return <DrilldownSidebarOrderFloodCert />;
    case loanDrilldownSidebarTypes.ORDER_FUNDING_SHIELD:
      return <DrilldownSidebarOrderFundingShield />;
    case loanDrilldownSidebarTypes.ORDER_INTERNAL_APPRAISAL:
      return <DrilldownSidebarOrderInternalAppraisal />;
    case loanDrilldownSidebarTypes.ORDER_OUTSIDE_VALUATION:
      return <DrilldownSidebarOrderOutsideValuation />;
    case loanDrilldownSidebarTypes.EMAIL_COMPOSER:
      return <DrilldownSidebarEmailComposer />;
    case loanDrilldownSidebarTypes.ADD_EDIT_LOAN_LOCK:
      return <DrilldownSidebarLoanLock />;
    default:
      return null;
  }
});

export default DrilldownSidebarContentRenderer;
